
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SearchFilters from './components/SearchFilters';
import { usePriceMonitor } from '../../hooks/usePriceMonitor';
import { productsData } from '../../mocks/products';
import { getAverageRating } from '../../mocks/reviews';

const ShopPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 3000]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [minRating, setMinRating] = useState(0);
  const [sortBy, setSortBy] = useState('relevance');
  const [trackedAnimation, setTrackedAnimation] = useState<number[]>([]);
  const { addToTracking, isTracked } = usePriceMonitor();

  const categories = ['All', ...Array.from(new Set(productsData.map(p => p.category)))];
  const brands = Array.from(new Set(productsData.map(p => p.brand)));
  const maxPrice = Math.max(...productsData.map(p => p.highestPrice));

  const filteredProducts = useMemo(() => {
    let result = productsData;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        p =>
          p.name.toLowerCase().includes(query) ||
          p.brand.toLowerCase().includes(query) ||
          p.category.toLowerCase().includes(query) ||
          p.description.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (selectedCategory !== 'All') {
      result = result.filter(p => p.category === selectedCategory);
    }

    // Price filter
    result = result.filter(p => {
      const lowestPrice = Math.min(...p.retailers.map(r => r.price));
      return lowestPrice >= priceRange[0] && lowestPrice <= priceRange[1];
    });

    // Brand filter
    if (selectedBrands.length > 0) {
      result = result.filter(p => selectedBrands.includes(p.brand));
    }

    // Rating filter
    if (minRating > 0) {
      result = result.filter(p => {
        const rating = getAverageRating(p.id);
        return rating >= minRating;
      });
    }

    // Sorting
    switch (sortBy) {
      case 'price-low':
        result = [...result].sort((a, b) => {
          const aPrice = Math.min(...a.retailers.map(r => r.price));
          const bPrice = Math.min(...b.retailers.map(r => r.price));
          return aPrice - bPrice;
        });
        break;
      case 'price-high':
        result = [...result].sort((a, b) => {
          const aPrice = Math.min(...a.retailers.map(r => r.price));
          const bPrice = Math.min(...b.retailers.map(r => r.price));
          return bPrice - aPrice;
        });
        break;
      case 'discount':
        result = [...result].sort((a, b) => a.priceChange - b.priceChange);
        break;
      case 'name':
        result = [...result].sort((a, b) => a.name.localeCompare(b.name));
        break;
    }

    return result;
  }, [searchQuery, selectedCategory, priceRange, selectedBrands, minRating, sortBy]);

  const handleTrackPrice = (product: typeof productsData[0]) => {
    const lowestRetailer = product.retailers.reduce((min, r) => (r.price < min.price ? r : min));
    addToTracking({
      id: product.id,
      name: product.name,
      image: product.image,
      currentPrice: lowestRetailer.price,
      retailer: lowestRetailer.name,
    });

    setTrackedAnimation(prev => [...prev, product.id]);
    setTimeout(() => {
      setTrackedAnimation(prev => prev.filter(id => id !== product.id));
    }, 1000);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All');
    setPriceRange([0, maxPrice]);
    setSelectedBrands([]);
    setMinRating(0);
    setSortBy('relevance');
  };

  const getLowestRetailer = (retailers: typeof productsData[0]['retailers']) => {
    return retailers.reduce((min, r) => (r.price < min.price ? r : min));
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(star => (
          <i
            key={star}
            className={`text-xs ${star <= Math.round(rating) ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-300'}`}
          ></i>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-teal-50 to-emerald-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Compare Prices & Save</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Find the best deals across multiple retailers. Track prices and get notified when they drop.
          </p>
          <div className="flex items-center justify-center gap-6 mt-6">
            <div className="flex items-center gap-2 text-gray-600">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-store-2-line text-xl text-teal-600"></i>
              </div>
              <span className="text-sm">50+ Retailers</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-refresh-line text-xl text-teal-600"></i>
              </div>
              <span className="text-sm">Hourly Updates</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <div className="w-8 h-8 flex items-center justify-center">
                <i className="ri-notification-3-line text-xl text-teal-600"></i>
              </div>
              <span className="text-sm">Price Alerts</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search & Filters */}
        <div className="mb-8">
          <SearchFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            priceRange={priceRange}
            onPriceRangeChange={setPriceRange}
            selectedBrands={selectedBrands}
            onBrandsChange={setSelectedBrands}
            minRating={minRating}
            onMinRatingChange={setMinRating}
            sortBy={sortBy}
            onSortChange={setSortBy}
            categories={categories}
            brands={brands}
            maxPrice={maxPrice}
            onClearFilters={handleClearFilters}
          />
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-gray-500">
            Showing <span className="font-medium text-gray-900">{filteredProducts.length}</span> products
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map(product => {
            const lowestRetailer = getLowestRetailer(product.retailers);
            const tracked = isTracked(product.id);
            const rating = getAverageRating(product.id);

            return (
              <div
                key={product.id}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
                data-product-shop
              >
                <Link to={`/product/${product.id}`} className="block cursor-pointer">
                  <div className="aspect-square bg-gray-50 p-4 relative">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain object-top"
                    />
                    <div className="absolute top-3 left-3 bg-green-500 text-white text-xs font-medium px-2 py-1 rounded">
                      {product.priceChange}% from peak
                    </div>
                  </div>
                </Link>
                <div className="p-5">
                  <p className="text-xs text-gray-500 mb-1">{product.category}</p>
                  <Link to={`/product/${product.id}`} className="cursor-pointer">
                    <h3 className="text-base font-semibold text-gray-900 mb-2 hover:text-teal-600 transition-colors line-clamp-1">
                      {product.name}
                    </h3>
                  </Link>

                  {/* Rating */}
                  {rating > 0 && (
                    <div className="flex items-center gap-2 mb-3">
                      {renderStars(rating)}
                      <span className="text-xs text-gray-500">{rating}</span>
                    </div>
                  )}

                  {/* Price Range */}
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-xl font-bold text-gray-900">${lowestRetailer.price}</span>
                      <span className="text-xs text-gray-500 ml-2">at {lowestRetailer.name}</span>
                    </div>
                  </div>

                  {/* Price History Bar */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>Price range</span>
                      <span>
                        ${product.lowestPrice} - ${product.highestPrice}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full relative">
                      <div
                        className="absolute h-full bg-gradient-to-r from-green-400 to-teal-500 rounded-full"
                        style={{
                          left: '0%',
                          width: `${((lowestRetailer.price - product.lowestPrice) / (product.highestPrice - product.lowestPrice)) * 100}%`,
                        }}
                      />
                      <div
                        className="absolute w-3 h-3 bg-teal-600 rounded-full -top-0.5 border-2 border-white shadow"
                        style={{
                          left: `${((lowestRetailer.price - product.lowestPrice) / (product.highestPrice - product.lowestPrice)) * 100}%`,
                          transform: 'translateX(-50%)',
                        }}
                      />
                    </div>
                  </div>

                  {/* Retailer Prices */}
                  <div className="space-y-2 mb-4">
                    {product.retailers.slice(0, 3).map((retailer, idx) => (
                      <a
                        key={idx}
                        href={retailer.url}
                        className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <span className="text-sm text-gray-700">{retailer.name}</span>
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-sm font-medium ${retailer.price === lowestRetailer.price ? 'text-green-600' : 'text-gray-900'}`}
                          >
                            ${retailer.price}
                          </span>
                          <i className="ri-external-link-line text-gray-400 text-sm"></i>
                        </div>
                      </a>
                    ))}
                  </div>

                  {/* Track Price Button */}
                  <button
                    onClick={() => handleTrackPrice(product)}
                    disabled={tracked}
                    className={`w-full py-2.5 px-4 rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap text-sm font-medium ${
                      tracked
                        ? 'bg-teal-100 text-teal-700 border border-teal-200'
                        : trackedAnimation.includes(product.id)
                          ? 'bg-teal-600 text-white scale-95'
                          : 'bg-teal-600 text-white hover:bg-teal-700'
                    }`}
                  >
                    {tracked ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-check-line"></i>
                        Tracking
                      </span>
                    ) : trackedAnimation.includes(product.id) ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-check-line"></i>
                        Added!
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-line-chart-line"></i>
                        Track Price
                      </span>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredProducts.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-search-line text-5xl text-gray-300"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search or filters</p>
            <button
              onClick={handleClearFilters}
              className="px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ShopPage;
